import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Pencil,
  Trash2,
  Plus,
  Check,
  X,
  StickyNote,
  Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useCompanies } from "@/hooks/useCompanies";
import {
  useEquipmentInstances,
  useCreateEquipmentInstance,
  useUpdateEquipmentInstance,
  useDeleteEquipmentInstance,
  type EquipmentInstance,
  type EquipmentInstanceStatus,
} from "@/hooks/useEquipmentInstances";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useColumnSelection,
  type SelectableColumn,
} from "@/hooks/useColumnSelection";
import { ColumnPicker } from "@/components/ColumnPicker";
import { formatDate } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import { htmlToPlainText } from "@/lib/html-to-plain-text";
import { useInstancesSimCounts, useSimDevices } from "@/hooks/useSims";
import { useSingleEquipment } from "@/hooks/useEquipment";
import { DeviceSimDialog } from "@/components/sims/DeviceSimDialog";

type InstanceStatus = EquipmentInstanceStatus;

interface EquipmentInstanceTableProps {
  equipmentId: string;
}

const statusColors: Record<InstanceStatus, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  repair:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  archived: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
};

type InstanceColumnKey =
  | "identifier"
  | "serial_number"
  | "status"
  | "acquisition_date"
  | "acquisition_price"
  | "vendor"
  | "qr_code"
  | "updated_at";

interface InstanceColumn extends SelectableColumn {
  key: InstanceColumnKey;
  className?: string;
}

/** All columns the instance table can show, in display order. */
const INSTANCE_COLUMNS: InstanceColumn[] = [
  {
    key: "identifier",
    label: "Identifier",
    required: true,
    defaultVisible: true,
  },
  {
    key: "serial_number",
    label: "Serial Number",
    defaultVisible: true,
    className: "min-w-[240px]",
  },
  { key: "status", label: "Status", defaultVisible: true },
  { key: "acquisition_date", label: "Acquisition Date", defaultVisible: true },
  { key: "acquisition_price", label: "Price", defaultVisible: true },
  { key: "vendor", label: "Vendor", defaultVisible: true },
  { key: "qr_code", label: "QR Code" },
  { key: "updated_at", label: "Last Changed" },
];

/**
 * Instance notes shown in full below the instance row. Clicking the text
 * switches to an auto-growing textarea.
 */
function InstanceNotes({
  instance,
  equipmentId,
  isEditing,
  onEditingChange,
}: {
  instance: EquipmentInstance;
  equipmentId: string;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}) {
  const updateInstance = useUpdateEquipmentInstance();
  // Legacy notes may contain HTML; show and edit them as plain text
  const saved = htmlToPlainText(instance.notes ?? "");
  const [draft, setDraft] = useState(saved);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Compare against the stored value so saving also cleans up legacy HTML
  const isDirty = draft !== (instance.notes ?? "");

  // Start every edit from the current value
  useEffect(() => {
    if (isEditing) setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Grow the textarea with its content
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [draft, isEditing]);

  const close = () => onEditingChange(false);

  const save = () => {
    if (!isDirty) return close();
    updateInstance.mutate(
      {
        id: instance.id,
        equipmentId,
        notes: draft.trim() ? draft : null,
      },
      { onSuccess: close },
    );
  };

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={() => onEditingChange(true)}
        title="Click to edit notes"
        className="block w-full whitespace-pre-wrap break-words rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        data-testid={`equipment-instance-notes-text-${instance.id}`}
      >
        {saved}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea
        ref={textareaRef}
        autoFocus
        aria-label={`Notes for instance ${instance.identifier}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
          } else if (e.key === "Escape") {
            e.preventDefault();
            close();
          }
        }}
        placeholder="Free text notes, e.g. damages, repairs, accessories..."
        className="min-h-[80px] resize-none overflow-hidden"
        data-testid={`equipment-instance-notes-${instance.id}`}
      />
      <div className="flex items-center justify-end gap-2">
        <span className="mr-auto text-xs text-muted-foreground">
          Ctrl/⌘ + Enter to save · Esc to cancel
        </span>
        <Button size="sm" variant="outline" onClick={close}>
          Cancel
        </Button>
        <Button
          size="sm"
          offlineEdit
          disabled={updateInstance.isPending}
          onClick={save}
          data-testid={`save-equipment-instance-notes-${instance.id}`}
        >
          Save notes
        </Button>
      </div>
    </div>
  );
}

export const EquipmentInstanceTable = ({
  equipmentId,
}: EquipmentInstanceTableProps) => {
  const { data: instances = [], isLoading } =
    useEquipmentInstances(equipmentId);
  const { data: companies = [] } = useCompanies();
  const createInstance = useCreateEquipmentInstance();
  const updateInstance = useUpdateEquipmentInstance();
  const deleteInstance = useDeleteEquipmentInstance();
  const isMobile = useIsMobile();
  const {
    columns: allColumns,
    visibleColumns,
    setColumnVisible,
    resetColumns,
  } = useColumnSelection("equipment-instance-columns", INSTANCE_COLUMNS);
  const [notesEditingIds, setNotesEditingIds] = useState<Set<string>>(
    () => new Set(),
  );

  const setNotesEditing = (id: string, editing: boolean) =>
    setNotesEditingIds((prev) => {
      const next = new Set(prev);
      if (editing) next.add(id);
      else next.delete(id);
      return next;
    });
  const { data: simDevices } = useSimDevices();
  const { data: equipment } = useSingleEquipment(equipmentId);
  const canHoldSims = simDevices.some((d) => d.equipmentID === equipmentId);
  const simCounts = useInstancesSimCounts(
    canHoldSims ? instances.map((i) => i.id) : [],
  );
  const [simInstance, setSimInstance] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const openSims = (instance: EquipmentInstance) =>
    setSimInstance({
      id: instance.id,
      name: `${equipment?.short_name?.trim() || equipment?.name || ""} ${instance.identifier}`.trim(),
    });
  const simDialog = (
    <DeviceSimDialog
      instance={simInstance}
      onOpenChange={(open) => !open && setSimInstance(null)}
    />
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    identifier: "",
    serial_number: "",
    notes: "",
    acquisition_date: "",
    acquisition_price: "",
    vendor_id: "",
    status: "active" as InstanceStatus,
  });

  const companyOptions = companies.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const resetForm = () => {
    setFormData({
      identifier: "",
      serial_number: "",
      notes: "",
      acquisition_date: "",
      acquisition_price: "",
      vendor_id: "",
      status: "active",
    });
    setEditingId(null);
    setIsCreating(false);
    setIsDialogOpen(false);
  };

  const openCreateDialog = () => {
    setFormData({
      identifier: "",
      serial_number: "",
      notes: "",
      acquisition_date: "",
      acquisition_price: "",
      vendor_id: "",
      status: "active",
    });
    setEditingId(null);
    setIsCreating(true);
    if (isMobile) {
      setIsDialogOpen(true);
    }
  };

  const openEditDialog = (instance: EquipmentInstance) => {
    setEditingId(instance.id);
    setFormData({
      identifier: instance.identifier,
      serial_number: instance.serial_number || "",
      notes: htmlToPlainText(instance.notes || ""),
      acquisition_date: instance.acquisition_date || "",
      acquisition_price: instance.acquisition_price?.toString() || "",
      vendor_id: instance.vendor_id || "",
      status: instance.status,
    });
    setIsCreating(false);
    if (isMobile) {
      setIsDialogOpen(true);
    }
  };

  const startEdit = (instance: EquipmentInstance) => {
    setEditingId(instance.id);
    setFormData({
      identifier: instance.identifier,
      serial_number: instance.serial_number || "",
      notes: htmlToPlainText(instance.notes || ""),
      acquisition_date: instance.acquisition_date || "",
      acquisition_price: instance.acquisition_price?.toString() || "",
      vendor_id: instance.vendor_id || "",
      status: instance.status,
    });
  };

  const handleSave = () => {
    const data = {
      identifier: formData.identifier,
      serial_number: formData.serial_number || null,
      notes: formData.notes || null,
      acquisition_date: formData.acquisition_date || null,
      acquisition_price: formData.acquisition_price
        ? Number(formData.acquisition_price)
        : null,
      vendor_id: formData.vendor_id || null,
      status: formData.status,
    };

    if (isCreating) {
      createInstance.mutate(
        { ...data, equipment_id: equipmentId },
        { onSuccess: resetForm },
      );
    } else if (editingId) {
      updateInstance.mutate(
        { ...data, id: editingId, equipmentId },
        { onSuccess: resetForm },
      );
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this instance?")) {
      deleteInstance.mutate({ id, equipmentId });
    }
  };

  const handleInlineFormKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    handleSave();
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading instances...
      </div>
    );
  }

  const isEditing = editingId !== null || isCreating;

  const renderFormFields = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="identifier">Identifier</Label>
        <Input
          id="identifier"
          value={formData.identifier}
          onChange={(e) =>
            setFormData({ ...formData, identifier: e.target.value })
          }
          placeholder="Identifier"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="serial_number">Serial Number</Label>
        <Input
          id="serial_number"
          value={formData.serial_number}
          onChange={(e) =>
            setFormData({ ...formData, serial_number: e.target.value })
          }
          placeholder="Serial Number"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value: InstanceStatus) =>
            setFormData({ ...formData, status: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="repair">Repair</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="acquisition_date">Acquisition Date</Label>
        <DatePicker
          id="acquisition_date"
          value={formData.acquisition_date}
          onChange={(v) => setFormData({ ...formData, acquisition_date: v })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="acquisition_price">Price</Label>
        <Input
          id="acquisition_price"
          type="number"
          step="0.01"
          value={formData.acquisition_price}
          onChange={(e) =>
            setFormData({ ...formData, acquisition_price: e.target.value })
          }
          placeholder="Price"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="vendor">Vendor</Label>
        <EntityCombobox
          options={companyOptions}
          value={formData.vendor_id}
          onChange={(value) =>
            setFormData({ ...formData, vendor_id: value || "" })
          }
          placeholder="Select vendor"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Notes"
          className="min-h-[160px]"
        />
      </div>
    </div>
  );

  // Mobile view with cards and dialog
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Instances ({instances.length})
          </h3>
          <Button offlineEdit onClick={openCreateDialog} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Instance
          </Button>
        </div>

        {instances.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 border rounded-lg">
            No instances yet. Click "Add Instance" to create one.
          </div>
        ) : (
          <div className="space-y-2">
            {instances.map((instance) => (
              <Card
                key={instance.id}
                className="cursor-pointer"
                onClick={() => openEditDialog(instance)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium">{instance.identifier}</p>
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[instance.status]}>
                          {instance.status}
                        </Badge>
                        {instance.acquisition_price && (
                          <span className="text-sm text-muted-foreground">
                            €{instance.acquisition_price}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {canHoldSims && (
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="SIM cards"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSims(instance);
                          }}
                        >
                          <Smartphone className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(instance.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {instance.notes && (
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                      {htmlToPlainText(instance.notes)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            if (!open) resetForm();
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isCreating ? "Add Instance" : "Edit Instance"}
              </DialogTitle>
            </DialogHeader>
            {renderFormFields()}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button offlineEdit onClick={handleSave}>
                {isCreating ? "Create" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {simDialog}
      </div>
    );
  }

  const statusSelect = (
    <Select
      value={formData.status}
      onValueChange={(value: InstanceStatus) =>
        setFormData({ ...formData, status: value })
      }
    >
      <SelectTrigger className="w-28">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="active">Active</SelectItem>
        <SelectItem value="repair">Repair</SelectItem>
        <SelectItem value="archived">Archived</SelectItem>
      </SelectContent>
    </Select>
  );

  const renderViewCell = (
    key: InstanceColumnKey,
    instance: EquipmentInstance,
  ): ReactNode => {
    switch (key) {
      case "identifier":
        return (
          <span
            className="font-medium"
            data-testid={`equipment-instance-identifier-${instance.id}`}
          >
            {instance.identifier}
          </span>
        );
      case "serial_number":
        return instance.serial_number || "-";
      case "status":
        return (
          <Badge className={statusColors[instance.status]}>
            {instance.status}
          </Badge>
        );
      case "acquisition_date":
        return instance.acquisition_date || "-";
      case "acquisition_price":
        return instance.acquisition_price
          ? `€${instance.acquisition_price}`
          : "-";
      case "vendor":
        return instance.vendor?.name || "-";
      case "qr_code":
        return instance.qr_code || "-";
      case "updated_at":
        return formatDate(instance.updated_at) || "-";
    }
  };

  const renderEditCell = (
    key: InstanceColumnKey,
    instance?: EquipmentInstance,
  ): ReactNode => {
    switch (key) {
      case "identifier":
        return (
          <Input
            value={formData.identifier}
            data-testid="equipment-instance-identifier-input"
            onKeyDown={handleInlineFormKeyDown}
            onChange={(e) =>
              setFormData({ ...formData, identifier: e.target.value })
            }
            placeholder="Identifier"
          />
        );
      case "serial_number":
        return (
          <Input
            value={formData.serial_number}
            data-testid="equipment-instance-serial-input"
            onKeyDown={handleInlineFormKeyDown}
            onChange={(e) =>
              setFormData({ ...formData, serial_number: e.target.value })
            }
            placeholder="Serial Number"
            className="min-w-[220px]"
          />
        );
      case "status":
        return statusSelect;
      case "acquisition_date":
        return (
          <DatePicker
            value={formData.acquisition_date}
            onChange={(v) => setFormData({ ...formData, acquisition_date: v })}
          />
        );
      case "acquisition_price":
        return (
          <Input
            type="number"
            step="0.01"
            value={formData.acquisition_price}
            data-testid="equipment-instance-price-input"
            onKeyDown={handleInlineFormKeyDown}
            onChange={(e) =>
              setFormData({ ...formData, acquisition_price: e.target.value })
            }
            placeholder="Price"
            className="w-24"
          />
        );
      case "vendor":
        return (
          <EntityCombobox
            options={companyOptions}
            value={formData.vendor_id}
            onChange={(value) =>
              setFormData({ ...formData, vendor_id: value || "" })
            }
            placeholder="Select vendor"
          />
        );
      default:
        // Read-only columns keep showing their value while editing
        return instance ? renderViewCell(key, instance) : "-";
    }
  };

  // Selectable columns + actions column
  const totalColumnCount = visibleColumns.length + 1;

  const renderEditRows = (instance?: EquipmentInstance) => (
    <>
      <TableRow className="border-b-0">
        {visibleColumns.map((column) => (
          <TableCell key={column.key} className={column.className}>
            {renderEditCell(column.key, instance)}
          </TableCell>
        ))}
        <TableCell>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSave}
              data-testid="save-equipment-instance"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={resetForm}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={totalColumnCount} className="pt-0">
          <Label htmlFor="instance-notes-input" className="sr-only">
            Notes
          </Label>
          <Textarea
            id="instance-notes-input"
            value={formData.notes}
            data-testid="equipment-instance-notes-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSave();
              }
            }}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="Notes"
            className="min-h-[120px] resize-y"
          />
        </TableCell>
      </TableRow>
    </>
  );

  // Desktop view with table, notes below each row and inline editing
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-2">
        <h3 className="text-lg font-semibold">
          Instances ({instances.length})
        </h3>
        <div className="flex items-center gap-2">
          <ColumnPicker
            columns={allColumns}
            visibleColumns={visibleColumns}
            onColumnVisibleChange={setColumnVisible}
            onReset={resetColumns}
            testIdPrefix="equipment-instance"
            size="sm"
          />
          <Button
            onClick={openCreateDialog}
            disabled={isEditing}
            size="sm"
            data-testid="add-equipment-instance"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Instance
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableHead key={column.key} className={column.className}>
                  {column.label}
                </TableHead>
              ))}
              <TableHead className={canHoldSims ? "w-36" : "w-28"}>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isCreating && renderEditRows()}
            {instances.map((instance) => {
              if (editingId === instance.id) {
                return (
                  <Fragment key={instance.id}>
                    {renderEditRows(instance)}
                  </Fragment>
                );
              }

              const isEditingNotes = notesEditingIds.has(instance.id);
              const showNotes = Boolean(instance.notes) || isEditingNotes;
              return (
                <Fragment key={instance.id}>
                  <TableRow
                    className={cn(showNotes && "border-b-0")}
                    data-testid={`equipment-instance-row-${instance.id}`}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        startEdit(instance);
                      }
                    }}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key} className={column.className}>
                        {renderViewCell(column.key, instance)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <div className="flex gap-1">
                        {canHoldSims && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-9 gap-1 px-2"
                            onClick={() => openSims(instance)}
                            disabled={isEditing}
                            title="SIM cards"
                            data-testid={`equipment-instance-sims-${instance.id}`}
                          >
                            <Smartphone className="h-4 w-4" />
                            <span className="text-xs tabular-nums">
                              {simCounts.get(instance.id) ?? 0}
                            </span>
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          title={instance.notes ? "Edit notes" : "Add notes"}
                          onClick={() => setNotesEditing(instance.id, true)}
                          disabled={isEditingNotes}
                          data-testid={`edit-equipment-instance-notes-${instance.id}`}
                        >
                          <StickyNote className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Edit instance"
                          onClick={() => startEdit(instance)}
                          disabled={isEditing}
                          data-testid={`edit-equipment-instance-${instance.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Delete instance"
                          onClick={() => handleDelete(instance.id)}
                          disabled={isEditing}
                          data-testid={`delete-equipment-instance-${instance.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {showNotes && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={totalColumnCount} className="pt-0">
                        <InstanceNotes
                          instance={instance}
                          equipmentId={equipmentId}
                          isEditing={isEditingNotes}
                          onEditingChange={(editing) =>
                            setNotesEditing(instance.id, editing)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {instances.length === 0 && !isCreating && (
              <TableRow>
                <TableCell
                  colSpan={totalColumnCount}
                  className="text-center text-muted-foreground py-8"
                >
                  No instances yet. Click "Add Instance" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {simDialog}
    </div>
  );
};
